import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { NotificationPanel } from '../ui/NotificationPanel';
import {
    LayoutDashboard,
    BookOpen,
    TrendingUp,
    ClipboardCheck,
    FolderOpen,
    Video,
    Award,
    LogOut,
    Menu,
    Search,
    Bell,
    ChevronDown,
    ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
    { key: 'dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { 
        key: 'learningPath', 
        icon: BookOpen, 
        path: '/learning',
        subItems: [
            { key: 'basic', path: '/learning/basic' },
            { key: 'intermediate', path: '/learning/intermediate' },
            { key: 'advanced', path: '/learning/advanced' },
            { key: 'expert', path: '/learning/expert' }
        ]
    },
    { 
        key: 'marketInsight', 
        icon: TrendingUp, 
        path: '/insights',
        subItems: [
            { key: 'weeklyBias', path: '/insights?category=weekly_bias' },
            { key: 'keyZones', path: '/insights?category=key_zones' },
            { key: 'newsImpact', path: '/insights?category=news_impact' }
        ]
    },
    { 
        key: 'checklist', 
        icon: ClipboardCheck, 
        path: '/checklist',
        subItems: [
            { key: 'preTrade', path: '/checklist?category=pre_trade' },
            { key: 'postTrade', path: '/checklist?category=post_trade' },
            { key: 'riskRules', path: '/checklist?category=risk_rules' }
        ]
    },
    { 
        key: 'resourceVault', 
        icon: FolderOpen, 
        path: '/resources',
        subItems: [
            { key: 'pdfNotes', path: '/resources?category=pdf_notes' },
            { key: 'tradingPlan', path: '/resources?category=trading_plan' },
            { key: 'riskCalculator', path: '/resources?category=risk_calculator' },
            { key: 'tradingJournal', path: '/resources?category=trading_journal' }
        ]
    },
    { 
        key: 'liveSession', 
        icon: Video, 
        path: '/sessions',
        subItems: [
            { key: 'weeklyReview', path: '/sessions?category=weekly_review' },
            { key: 'qaReplay', path: '/sessions?category=qa_replay' }
        ]
    },
    { key: 'certificate', icon: Award, path: '/certificates' }
];

export const DashboardLayout = ({ children }) => {
    const { logout, user } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedItems, setExpandedItems] = useState({});
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        fetchUnreadCount();
        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const response = await axios.get(`${API}/notifications/unread-count`);
            setUnreadCount(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const toggleExpand = (key) => {
        setExpandedItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
        }
    };

    const isActive = (path) => {
        if (path.includes('?')) {
            return location.pathname + location.search === path;
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const handleNotificationClose = () => {
        setNotificationOpen(false);
        // Refresh unread count when panel closes
        fetchUnreadCount();
    };

    const NavContent = () => (
        <div className="flex flex-col h-full bg-white">
            {/* Logo */}
            <div className="p-6">
                <Link to="/dashboard" className="flex items-center gap-3">
                    <img 
                        src="/assets/logo.jpg" 
                        alt="Sekolah NusaTraders" 
                        className="w-12 h-12 rounded-xl object-contain shadow-lg"
                    />
                    <div>
                        <span className="font-heading font-bold text-lg text-slate-900 block leading-tight">SEKOLAH</span>
                        <span className="font-heading font-bold text-sm text-primary block leading-tight">NUSATRADERS</span>
                    </div>
                </Link>
            </div>

            {/* Navigation */}
            <ScrollArea className="flex-1 px-4 py-2">
                <nav className="space-y-1">
                    {navItems.map((item) => (
                        <div key={item.key}>
                            {item.subItems ? (
                                <div>
                                    <button
                                        onClick={() => toggleExpand(item.key)}
                                        className={`w-full sidebar-item justify-between ${isActive(item.path) ? 'active' : ''}`}
                                        data-testid={`nav-${item.key}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                            <span className="font-medium">{t(item.key)}</span>
                                        </div>
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${expandedItems[item.key] ? 'rotate-180' : ''}`} />
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ${expandedItems[item.key] ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="ml-8 mt-1 space-y-1 py-1">
                                            {item.subItems.map((subItem) => (
                                                <Link
                                                    key={subItem.key}
                                                    to={subItem.path}
                                                    onClick={() => setMobileOpen(false)}
                                                    className={`block px-4 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                                                        isActive(subItem.path)
                                                            ? 'text-primary font-medium bg-primary/5'
                                                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                                    }`}
                                                    data-testid={`nav-sub-${subItem.key}`}
                                                >
                                                    {t(subItem.key)}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <Link
                                    to={item.path}
                                    onClick={() => setMobileOpen(false)}
                                    className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                                    data-testid={`nav-${item.key}`}
                                >
                                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                                    <span className="font-medium">{t(item.key)}</span>
                                </Link>
                            )}
                        </div>
                    ))}
                </nav>
            </ScrollArea>

            {/* User & Logout */}
            <div className="p-4 border-t border-slate-100">
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-slate-50">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm">
                            {user?.name?.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                    onClick={logout}
                    data-testid="logout-btn"
                >
                    <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
                    {t('logout')}
                </Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col glass-card !rounded-none border-r border-slate-100/50">
                <NavContent />
            </aside>

            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-card !rounded-none border-b border-slate-100/50">
                <div className="flex items-center justify-between px-4 h-16">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="mobile-menu-btn">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="p-0 w-72">
                            <NavContent />
                        </SheetContent>
                    </Sheet>

                    <Link to="/dashboard" className="flex items-center gap-2">
                        <img 
                            src="/assets/logo.jpg" 
                            alt="Sekolah NusaTraders" 
                            className="w-10 h-10 rounded-lg object-contain"
                        />
                        <span className="font-heading font-bold text-sm text-slate-900">SEKOLAH NUSATRADERS</span>
                    </Link>

                    {/* Language Toggle */}
                    <div className="lang-toggle" data-testid="lang-toggle">
                        <button
                            onClick={() => language !== 'id' && toggleLanguage()}
                            className={language === 'id' ? 'active' : ''}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => language !== 'en' && toggleLanguage()}
                            className={language === 'en' ? 'active' : ''}
                        >
                            EN
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="lg:pl-72">
                {/* Top Bar (Desktop) */}
                <div className="hidden lg:flex items-center justify-between px-8 py-4 glass-card !rounded-none border-b border-slate-100/50 sticky top-0 z-40">
                    <form onSubmit={handleSearch} className="relative w-96">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="search"
                            placeholder={t('searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-11 h-11 bg-slate-50/50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary/20"
                            data-testid="search-input"
                        />
                    </form>

                    <div className="flex items-center gap-3">
                        {/* Notification Bell */}
                        <div className="relative">
                            <Button 
                                variant="ghost" 
                                size="icon"
                                className="rounded-xl relative"
                                onClick={() => setNotificationOpen(!notificationOpen)}
                                data-testid="notification-btn"
                            >
                                <Bell className="w-5 h-5 text-slate-500" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-medium">
                                        {unreadCount}
                                    </span>
                                )}
                            </Button>
                            <NotificationPanel 
                                isOpen={notificationOpen}
                                onClose={handleNotificationClose}
                            />
                        </div>

                        {/* Language Toggle */}
                        <div className="lang-toggle" data-testid="lang-toggle-desktop">
                            <button
                                onClick={() => language !== 'id' && toggleLanguage()}
                                className={language === 'id' ? 'active' : ''}
                            >
                                ID
                            </button>
                            <button
                                onClick={() => language !== 'en' && toggleLanguage()}
                                className={language === 'en' ? 'active' : ''}
                            >
                                EN
                            </button>
                        </div>
                    </div>
                </div>

                {/* Page Content */}
                <div className="p-4 lg:p-8 pt-20 lg:pt-8">
                    {children}
                </div>
            </main>
        </div>
    );
};
